import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function Hero() {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      id: 1,
      title: 'VIP Program',
      subtitle: 'Exclusive Benefits & Special Rewards',
      description: 'Exclusive bonuses, fast withdrawals, dedicated manager',
      buttonText: 'Join Now',
      bgVideo: 'https://public.readdy.ai/ai/video_res/a915c31c-be2f-4f24-a632-8ebc352471b3.mp4',
      type: 'video',
    },
    {
      id: 3,
      title: 'Jackpot Moment',
      subtitle: 'Your Luck Starts Here',
      description: 'New winners are born every day',
      buttonText: 'Try Your Luck',
      bgVideo: 'https://public.readdy.ai/ai/video_res/d9282f96-7a69-40fa-9df0-215f49840ee6.mp4',
      type: 'video',
    },
    {
      id: 4,
      title: 'Premium Gaming Experience',
      subtitle: 'Best Slot Game Collection',
      description: 'Over 1000 premium games',
      buttonText: 'Start Playing',
      bgVideo: 'https://public.readdy.ai/ai/video_res/bc5f0e51-5552-4fbe-b5c6-cf2a1c30f4a7.mp4',
      type: 'video',
    },
  ];

  useEffect(() => {
    if (slides.length > 1) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 6000);
      return () => clearInterval(timer);
    }
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const handleScroll = () => {
    document.getElementById('winners')?.scrollIntoView({ behavior: 'smooth', block: "start" });
  };

  return (
    <div className="relative w-full h-[450px] md:h-[520px] overflow-hidden">
      {/* Slides */}
      <div className="relative w-full h-full">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
          >
            {/* Background Image or Video */}
            <div className="absolute inset-0">
              {slide.type === 'video' ? (
                <video
                  src={slide.bgVideo}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-cover object-center"
                />
              ) : (
                <img
                  src={slide.bgImage}
                  alt={slide.title}
                  className="w-full h-full object-cover object-top"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
            </div>

            {/* Content */}
            <div className="relative h-full flex items-center justify-center px-4">
              <div className="text-center text-white max-w-4xl">
                <h1 className="text-2xl md:text-5xl font-bold mb-3 md:mb-4 drop-shadow-lg">
                  {slide.title}
                </h1>
                <p className="text-lg md:text-2xl mb-2 md:mb-3 drop-shadow-lg font-medium">
                  {slide.subtitle}
                </p>
                <p className="text-sm md:text-lg mb-6 md:mb-8 drop-shadow-lg opacity-90">
                  {slide.description}
                </p>
                <button onClick={handleScroll} className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-5 py-2.5 md:px-8 md:py-3.5 rounded-full font-semibold text-sm md:text-base transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer">
                  {slide.buttonText}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer z-10"
            aria-label="Previous slide"
          >
            <i className="ri-arrow-left-s-line text-xl md:text-2xl"></i>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer z-10"
            aria-label="Next slide"
          >
            <i className="ri-arrow-right-s-line text-xl md:text-2xl"></i>
          </button>
        </>
      )}

      {/* Indicators */}
      {slides.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${index === currentSlide
                  ? 'bg-amber-500 w-8'
                  : 'bg-white/50 hover:bg-white/80'
                }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
